'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram, CostMetadata, Currency } from '@/lib/archimate/extended-types';
import {
  calculateDiagramTotalCost,
  getCostByLayer,
  getTopCostElements,
  formatCurrency,
  getCostHeatmapData,
} from '@/lib/archimate/cost-utils';
import { X, DollarSign, TrendingUp, PieChart, Download } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface CostPanelProps {
  diagram: ExtendedArchiMateDiagram;
  selectedElementId?: string;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onToggleHeatmap: (enabled: boolean) => void;
}

export default function CostPanel({
  diagram,
  selectedElementId,
  onUpdate,
  onClose,
  onToggleHeatmap,
}: CostPanelProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'element' | 'heatmap'>('overview');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Form state for element cost
  const [elementCost, setElementCost] = useState<CostMetadata>({
    infrastructure: 0,
    licensing: 0,
    team: 0,
    maintenance: 0,
    other: 0,
    currency: 'USD',
    period: 'monthly',
    lifespanYears: 3,
    implementationCost: 0,
  });

  const totalCosts = calculateDiagramTotalCost(diagram);
  const costsByLayer = getCostByLayer(diagram);
  const topCosts = getTopCostElements(diagram, 5);

  const handleSaveElementCost = () => {
    if (!selectedElementId) return;

    const updatedDiagram = {
      ...diagram,
      elementMetadata: {
        ...diagram.elementMetadata,
        [selectedElementId]: {
          ...diagram.elementMetadata[selectedElementId],
          cost: elementCost,
        },
      },
    };

    onUpdate(updatedDiagram);
  };

  const handleToggleHeatmap = () => {
    const newState = !showHeatmap;
    setShowHeatmap(newState);
    onToggleHeatmap(newState);
  };

  const handleExportCSV = () => {
    let csv = 'Element,Layer,Type,Infrastructure,Licensing,Team,Maintenance,Other,Monthly Total,Annual Total\n';

    diagram.elements.forEach((element) => {
      const metadata = diagram.elementMetadata[element.id];
      if (metadata?.cost) {
        const cost = metadata.cost;
        const monthly = cost.infrastructure + cost.licensing + cost.team + cost.maintenance + cost.other;
        csv += `"${element.name}",${element.layer},${element.type},`;
        csv += `${cost.infrastructure},${cost.licensing},${cost.team},${cost.maintenance},${cost.other},`;
        csv += `${monthly},${monthly * 12}\n`;
      }
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-costs.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load element cost if an element is selected
  React.useEffect(() => {
    if (selectedElementId) {
      const metadata = diagram.elementMetadata[selectedElementId];
      if (metadata?.cost) {
        setElementCost(metadata.cost);
      } else {
        // Reset to defaults
        setElementCost({
          infrastructure: 0,
          licensing: 0,
          team: 0,
          maintenance: 0,
          other: 0,
          currency: 'USD',
          period: 'monthly',
          lifespanYears: 3,
          implementationCost: 0,
        });
      }
      setViewMode('element');
    }
  }, [selectedElementId, diagram.elementMetadata]);

  const selectedElement = selectedElementId
    ? diagram.elements.find((e) => e.id === selectedElementId)
    : null;

  return (
    <div className="fixed right-0 top-0 h-screen w-[500px] bg-background border-l border-border flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Cost Modeling</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-cream/10 rounded transition-colors">
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="cost"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'overview' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('element')}
            disabled={!selectedElementId}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'element' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Element
          </button>
          <button
            onClick={handleToggleHeatmap}
            className={`ml-auto px-4 py-2 rounded transition-colors flex items-center gap-2 ${
              showHeatmap ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            <PieChart className="w-4 h-4" />
            Heatmap
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview */}
        {viewMode === 'overview' && (
          <div className="space-y-6">
            {/* Total Cost Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-cream/5 border border-border rounded">
                <div className="text-sm text-foreground-secondary mb-1">Monthly</div>
                <div className="text-2xl font-bold text-accent">
                  {formatCurrency(totalCosts.monthly, totalCosts.currency)}
                </div>
              </div>
              <div className="p-4 bg-cream/5 border border-border rounded">
                <div className="text-sm text-foreground-secondary mb-1">Annual</div>
                <div className="text-2xl font-bold text-accent">
                  {formatCurrency(totalCosts.annual, totalCosts.currency)}
                </div>
              </div>
              <div className="col-span-2 p-4 bg-accent-muted border border-border rounded">
                <div className="text-sm text-foreground-secondary mb-1">Total Cost of Ownership (3 years)</div>
                <div className="text-3xl font-bold text-accent">
                  {formatCurrency(totalCosts.tco, totalCosts.currency)}
                </div>
              </div>
            </div>

            {/* Cost by Layer */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Cost by Layer
              </h3>
              <div className="space-y-2">
                {Object.entries(costsByLayer)
                  .sort(([, a], [, b]) => b - a)
                  .map(([layer, cost]) => (
                    <div key={layer} className="flex items-center gap-3">
                      <div className="w-24 text-sm text-foreground-secondary capitalize">{layer}</div>
                      <div className="flex-1 h-8 bg-cream/10 rounded overflow-hidden">
                        <div
                          className="h-full bg-accent-muted0"
                          style={{
                            width: `${(cost / totalCosts.tco) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="w-32 text-right text-sm text-foreground font-medium">
                        {formatCurrency(cost, totalCosts.currency)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Top Cost Elements */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-3">Top 5 Expensive Components</h3>
              <div className="space-y-2">
                {topCosts.map(({ element, cost }, index) => (
                  <div
                    key={element.id}
                    className="p-3 bg-cream/5 border border-border rounded flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-foreground">
                        {index + 1}. {element.name}
                      </div>
                      <div className="text-sm text-foreground-tertiary">{element.type}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-accent">{formatCurrency(cost, totalCosts.currency)}</div>
                      <div className="text-xs text-foreground-tertiary">TCO</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Export */}
            <button
              onClick={handleExportCSV}
              className="w-full px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Cost Data (CSV)
            </button>
          </div>
        )}

        {/* Element Cost Editor */}
        {viewMode === 'element' && selectedElement && (
          <div className="space-y-4">
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="font-bold text-foreground">{selectedElement.name}</h3>
              <p className="text-sm text-foreground-tertiary">
                {selectedElement.type} • {selectedElement.layer}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Currency</label>
                <select
                  value={elementCost.currency}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, currency: e.target.value as Currency })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="JPY">JPY (¥)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Infrastructure (monthly)
                </label>
                <input
                  type="number"
                  value={elementCost.infrastructure}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, infrastructure: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Licensing (monthly)
                </label>
                <input
                  type="number"
                  value={elementCost.licensing}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, licensing: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Team (monthly FTE cost)</label>
                <input
                  type="number"
                  value={elementCost.team}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, team: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Maintenance (monthly)
                </label>
                <input
                  type="number"
                  value={elementCost.maintenance}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, maintenance: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Other (monthly)</label>
                <input
                  type="number"
                  value={elementCost.other}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, other: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                />
              </div>

              <div className="border-t border-border pt-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Implementation Cost (one-time)
                  </label>
                  <input
                    type="number"
                    value={elementCost.implementationCost}
                    onChange={(e) =>
                      setElementCost({
                        ...elementCost,
                        implementationCost: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Lifespan (years for TCO)
                </label>
                <input
                  type="number"
                  value={elementCost.lifespanYears}
                  onChange={(e) =>
                    setElementCost({ ...elementCost, lifespanYears: parseInt(e.target.value) || 3 })
                  }
                  min="1"
                  max="10"
                  className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                />
              </div>

              {/* Cost Summary */}
              <div className="p-4 bg-accent-muted border border-border rounded space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-secondary">Monthly Total:</span>
                  <span className="font-bold text-accent">
                    {formatCurrency(
                      elementCost.infrastructure +
                        elementCost.licensing +
                        elementCost.team +
                        elementCost.maintenance +
                        elementCost.other,
                      elementCost.currency
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-secondary">Annual Total:</span>
                  <span className="font-bold text-accent">
                    {formatCurrency(
                      (elementCost.infrastructure +
                        elementCost.licensing +
                        elementCost.team +
                        elementCost.maintenance +
                        elementCost.other) *
                        12,
                      elementCost.currency
                    )}
                  </span>
                </div>
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="text-foreground-secondary">Total Cost of Ownership:</span>
                  <span className="font-bold text-accent text-lg">
                    {formatCurrency(
                      (elementCost.implementationCost || 0) +
                        (elementCost.infrastructure +
                          elementCost.licensing +
                          elementCost.team +
                          elementCost.maintenance +
                          elementCost.other) *
                          12 *
                          (elementCost.lifespanYears || 3),
                      elementCost.currency
                    )}
                  </span>
                </div>
              </div>

              <button
                onClick={handleSaveElementCost}
                className="w-full px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover font-medium"
              >
                Save Cost Data
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
